import React, { useEffect, useMemo, useState } from "react";
import {
	TextField,
	Button,
	Box,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Typography,
	Tabs,
	Tab,
	Switch,
	FormControlLabel,
	Alert,
	Grid,
} from "@mui/material";
import Swal from "sweetalert2";
import { ALERT_MESSAGES, BUTTON, LABELS, MENU_ITEMS, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/authContext";
import { getOptions, insertOptions } from "../../services/defaultValuesService";
import { useOptions } from "../../context/optionContext";
import ContentInfo from "../ContentInfo/ContentInfo";
import { HelpCenterOutlined, HelpOutlineOutlined, Save } from "@mui/icons-material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

const DEFAULT_ORDER_SEQUENCE = {
	prefix: "ITH-",
	divider: "",
	suffix: "",
	includeDate: true,
	padLength: 6,
};

const DEFAULT_REQUEST_SEQUENCE = {
	prefix: "RL-",
	divider: "",
	suffix: "",
	includeDate: true,
	padLength: 6,
};

const parseSequenceConfig = (value, defaults) => {
	if (!value) return { ...defaults };
	let parsed = value;
	try {
		if (typeof value === "string") {
			parsed = JSON.parse(value);
		}
	} catch (error) {
		parsed = null;
	}
	if (!parsed || typeof parsed !== "object") return { ...defaults };
	const padLength = Number(parsed.padLength);
	return {
		...defaults,
		...parsed,
		padLength: Number.isFinite(padLength) && padLength > 0 ? padLength : defaults.padLength,
		includeDate: !(parsed.includeDate === false || parsed.includeDate === "false"),
		divider: typeof parsed.divider === "string" ? parsed.divider : defaults.divider,
	};
};

const buildSequencePreview = (config) => {
	const padLength = Math.max(1, Number(config?.padLength) || 6);
	const baseNumber = String(1).padStart(padLength, "0");
	const dateBlock = config?.includeDate ? "2501" : "";
	const parts = [];
	if (config?.prefix) parts.push(config.prefix);
	if (dateBlock) parts.push(dateBlock);
	parts.push(baseNumber);
	if (config?.suffix) parts.push(config.suffix);
	const divider = typeof config?.divider === "string" ? config.divider : "";
	return parts.filter(Boolean).join(divider);
};

const isEveryLoginMfaValue = (value) => {
	if (typeof value !== "string") return false;
	const normalized = value.trim().toLowerCase().replace(/[\s-]+/g, "_");
	return normalized === "every_login";
};

const normalizeMfaDaysValue = (value, fallback = 1) => {
	if (isEveryLoginMfaValue(value)) return "every_login";
	const n = Number(value);
	if (Number.isFinite(n) && n > 0) return Math.floor(n);
	return fallback;
};

const DefaultValues = () => {
	const [formData, setFormData] = useState({
		currency: "USD",
		minOrderAmount: 0,
		maxOrderQty: 999,
		creditCardFee: 2.5,
		taxRate: 5,
		shippingCost: 10,
			leadTime: 3,
			mfaDays: 1,
		mfaEnabled: "Enabled",
		ccPayment: "Enabled",
		trackInventory: "Enabled",
		backOrders: "Disabled",
		enableDiscountField: false,
		productReminder: 'Disabled',
		maximumReminderCount: 5,
		reminderPurchaseCountThreshold: 4,
		cartRetentionEnabled: true,
		cartRetentionDays: 60,
		defaultOrderNotifyEmails: "",
		defaultRlNotifyEmails: "",
		defaultCcrNotifyEmails: "",
	});
	const {t} = useTranslation();
	const { user, hasPermission } = useAuth();
	const { fetchOptions: refreshOptions } = useOptions();
	const [activeTab, setActiveTab] = useState(0);
	const [sequenceConfig, setSequenceConfig] = useState({
		orderNumberConfig: DEFAULT_ORDER_SEQUENCE,
		requestNumberConfig: DEFAULT_REQUEST_SEQUENCE,
	});

	useEffect(() => {
		const loadOptions = async () => {
			try {
				const response = await getOptions();
				// API shape: { status, message, data: { data: [...], options: {...} } }
				const payload = response?.data;
				const optionsArray = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
				const optionsObject = payload?.options || {};
				console.log("Options Response Data: ", optionsArray);

				const sequenceUpdates = {};
				const updatedFromOptions = optionsArray.reduce((acc, item) => {
					if (!item || !item.option_key) return acc;
					const key = item.option_key;
					const rawValue = item.option_value;

					if (key === "orderNumberConfig") {
						sequenceUpdates.orderNumberConfig = parseSequenceConfig(rawValue, DEFAULT_ORDER_SEQUENCE);
						return acc;
					}

					if (key === "requestNumberConfig") {
						sequenceUpdates.requestNumberConfig = parseSequenceConfig(rawValue, DEFAULT_REQUEST_SEQUENCE);
						return acc;
					}

						// Explicit handling for known types
						if (["minOrderAmount", "maxOrderQty", "creditCardFee", "taxRate", "shippingCost", "leadTime", "reminderPurchaseCountThreshold", "maximumReminderCount"].includes(key)) {
							return {
								...acc,
								[key]: Number(rawValue),
							};
						}
						if (key === "mfaDays") {
							return {
								...acc,
								[key]: normalizeMfaDaysValue(rawValue, 1),
							};
						}

					if (["enableDiscountField"].includes(key)) {
						return {
							...acc,
							[key]: rawValue === true || rawValue === "true",
						};
					}

					if (key === "cartRetentionEnabled") {
						return {
							...acc,
							[key]: rawValue === true || rawValue === "true",
						};
					}

					if (key === "cartRetentionDays") {
						return {
							...acc,
							[key]: Number(rawValue) || 60,
						};
					}

					// Keep string toggles as-is (Enabled/Disabled, currency, etc.)
					return {
						...acc,
						[key]: isNaN(rawValue) ? rawValue : Number(rawValue),
					};
				}, {});

				const emailDefaults = ["defaultOrderNotifyEmails", "defaultRlNotifyEmails", "defaultCcrNotifyEmails"];
				emailDefaults.forEach((key) => {
					if (!Object.prototype.hasOwnProperty.call(updatedFromOptions, key) && optionsObject?.[key]) {
						updatedFromOptions[key] = optionsObject[key];
					}
				});

				setFormData((prevData) => ({
					...prevData,
					...updatedFromOptions,
				}));
				if (Object.keys(sequenceUpdates).length) {
					setSequenceConfig((prev) => ({
						...prev,
						...sequenceUpdates,
					}));
				}
			} catch (error) {
				console.log(error);
			}
		};
		loadOptions();
	}, []);

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prevData) => ({
			...prevData,
			[name]: value,
		}));
	};

	const handleSwitchChange = (event) => {
		const { name, checked } = event.target;
		setFormData((prevData) => ({
			...prevData,
			[name]: checked,
		}));
	};

	const handleMfaFrequencyModeChange = (event) => {
		const mode = event.target.value;
		const currentDays = formData?.mfaDays;
		const nextDaysValue =
			mode === "every_login"
				? "every_login"
				: (isEveryLoginMfaValue(currentDays) ? 1 : normalizeMfaDaysValue(currentDays, 1));
		setFormData((prevData) => ({
			...prevData,
			mfaDays: nextDaysValue,
		}));
	};

	const handleSequenceChange = (key, field) => (event) => {
		const defaults = key === "requestNumberConfig" ? DEFAULT_REQUEST_SEQUENCE : DEFAULT_ORDER_SEQUENCE;
		const rawValue = field === "includeDate" ? event.target.checked : event.target.value;
		let normalizedValue = rawValue;

		if (field === "padLength") {
			const parsed = parseInt(rawValue, 10);
			normalizedValue = Math.max(1, Number.isFinite(parsed) ? parsed : defaults.padLength);
		}

		setSequenceConfig((prev) => ({
			...prev,
			[key]: {
				...prev[key],
				[field]: normalizedValue,
			},
		}));
	};

	const resetSequence = (key) => {
		const defaults = key === "requestNumberConfig" ? DEFAULT_REQUEST_SEQUENCE : DEFAULT_ORDER_SEQUENCE;
		setSequenceConfig((prev) => ({
			...prev,
			[key]: { ...defaults },
		}));
	};

	const renderSequenceCard = (title, key, defaults) => {
		const config = sequenceConfig[key] || defaults;
		const preview = buildSequencePreview(config);
		return (
			<Box
				sx={{
					border: "1px solid",
					borderColor: "divider",
					borderRadius: 2,
					p: 2,
					display: "flex",
					flexDirection: "column",
					gap: 2,
				}}
			>
				<Typography variant="h6">{title}</Typography>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6}>
						<TextField
							label={t('LABELS.PREFIX', { defaultValue: "Prefix" })}
							value={config?.prefix || ""}
							onChange={handleSequenceChange(key, "prefix")}
							fullWidth
							size="small"
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							label={t('LABELS.DIVIDER', { defaultValue: "Divider" })}
							value={config?.divider || ""}
							onChange={handleSequenceChange(key, "divider")}
							fullWidth
							size="small"
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							label={t('LABELS.SUFFIX', { defaultValue: "Suffix" })}
							value={config?.suffix || ""}
							onChange={handleSequenceChange(key, "suffix")}
							fullWidth
							size="small"
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							label={t('LABELS.DIGITS', { defaultValue: "Digits" })}
							type="number"
							value={config?.padLength || 6}
							onChange={handleSequenceChange(key, "padLength")}
							fullWidth
							size="small"
							inputProps={{ min: 1 }}
						/>
					</Grid>
				</Grid>
				<FormControlLabel
					control={
						<Switch
							checked={!!config?.includeDate}
							onChange={handleSequenceChange(key, "includeDate")}
						/>
					}
					label={t('LABELS.INCLUDE_DATE', { defaultValue: "Include date (YYMM)" })}
				/>
				<Alert severity="info">
					{t('UI_TEXT.EXAMPLE', { defaultValue: "Example" })}: <strong>{preview}</strong>
				</Alert>
				<Button
					startIcon={<RestartAltIcon />}
					onClick={() => resetSequence(key)}
					variant="text"
				>
					{t('BUTTON.RESTORE_DEFAULTS', { defaultValue: "Restore defaults" })}
				</Button>
			</Box>
		);
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		const req_body = Object.entries(formData).map(([key, value]) => ({
			option_key: key,
			option_value: value,
		}));
		req_body.push(
			{
				option_key: "orderNumberConfig",
				option_value: JSON.stringify(sequenceConfig.orderNumberConfig),
			},
			{
				option_key: "requestNumberConfig",
				option_value: JSON.stringify(sequenceConfig.requestNumberConfig),
			},
		);

		try {
			const resp = await insertOptions({ options: req_body });
			if (resp?.status) {
				Swal.fire({
					icon: "success",
					title: t('ALERT_MESSAGES.SUCCESS') || "Success",
					text: (t('ALERT_MESSAGES.OPTION_SAVED_SUCCESSFULLY') || t('ALERT_MESSAGES.OPTION_SAVED_SUCCESSFULLY') || resp?.message ),
					confirmButtonText: t('UI_TEXT.OKAY'),
				});
				if (typeof refreshOptions === "function") {
					await refreshOptions();
				}
			} else {
				Swal.fire({
					icon: "error",
					title: t('ALERT_MESSAGES.ERROR'),
					text: resp?.message || t('UI_TEXT.FAILED_TO_SAVE_OPTIONS'),
				});
			}
		} catch (error) {
			console.log(error);
			Swal.fire({
				icon: "error",
				title: t('ALERT_MESSAGES.ERROR'),
				text: error?.response?.data?.message || error?.message || t('UI_TEXT.FAILED_TO_SAVE_OPTIONS'),
			});
		}
		// Handle the submission (e.g., API call)
	};

	const settingsTabs = useMemo(() => ([
		// {
		// 	key: "order",
		// 	label: t('SETTINGS_TABS.ORDER'),
		// 	permission: 'app_settings.manage_order_settings',
		// 	infoKey: 'SETTINGS_INFO.ORDER',
		// 	content: (
		// 		<>
		// 			<Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
		// 				{t('UI_TEXT.ORDER_SETTINGS')}
		// 			</Typography>
		// 			<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
		// 				{t('UI_TEXT.ORDER_SETTINGS_DESC', { defaultValue: "Configure default currency, minimum order values, payment modes, and shipping/tax defaults used during checkout." })}
		// 			</Typography>
		// 			<Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, mb: 1 }}>
		// 				<FormControl fullWidth size="small">
		// 					<InputLabel>{t('UI_TEXT.DEFAULT_CURRENCY')}</InputLabel>
		// 					<Select
		// 						name="currency"
		// 						value={formData?.currency || ""}
		// 						onChange={handleChange}
		// 						label={t('LABELS.DEFAULT_CURRENCY')}
		// 						size="small">
		// 						<MenuItem value="USD">USD</MenuItem>
		// 						<MenuItem value="EUR">EUR</MenuItem>
		// 						<MenuItem value="GBP">GBP</MenuItem>
		// 						<MenuItem value="INR">INR</MenuItem>
		// 					</Select>
		// 				</FormControl>
		// 				<TextField
		// 					label={t('LABELS.MINIMUM_ORDER_AMOUNT')}
		// 					variant="outlined"
		// 					name="minOrderAmount"
		// 					type="number"
		// 					value={formData?.minOrderAmount || ""}
		// 					onChange={handleChange}
		// 					size="small"
		// 					fullWidth
		// 				/>
		// 				<TextField
		// 					label={t('LABELS.MAX_ORDER_QTY', { defaultValue: "Max Order Quantity" })}
		// 					variant="outlined"
		// 					name="maxOrderQty"
		// 					type="number"
		// 					value={formData?.maxOrderQty ?? ""}
		// 					onChange={handleChange}
		// 					size="small"
		// 					fullWidth
		// 					inputProps={{ min: 1 }}
		// 				/>
		// 			</Box>

		// 			<Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
		// 				{t('UI_TEXT.PAYMENT_MODES')}
		// 			</Typography>
		// 			<Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, mb: 1 }}>
		// 				<FormControl fullWidth size="small">
		// 					<InputLabel>{t('UI_TEXT.CREDIT_CARD_PAYMENTS')}</InputLabel>
		// 					<Select
		// 						name="ccPayment"
		// 						value={formData?.ccPayment || ""}
		// 						onChange={handleChange}
		// 						label={t('LABELS.CREDIT_CARD_PAYMENTS')}
		// 						size="small">
		// 						<MenuItem value="Enabled">{t('MENU_ITEMS.ENABLED')}</MenuItem>
		// 						<MenuItem value="Disabled">{t('MENU_ITEMS.DISABLED')}</MenuItem>
		// 					</Select>
		// 				</FormControl>
		// 				<TextField
		// 					label={t('LABELS.CREID_CARD_FEES')}
		// 					variant="outlined"
		// 					name="creditCardFee"
		// 					type="number"
		// 					value={formData?.creditCardFee || ""}
		// 					onChange={handleChange}
		// 					size="small"
		// 					fullWidth
		// 				/>
		// 			</Box>

		// 			<Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
		// 				{t('UI_TEXT.SHIPPING_TAXES')}
		// 			</Typography>
		// 			<Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
		// 				<TextField
		// 					label={t('LABELS.SHIPPING_COST')}
		// 					variant="outlined"
		// 					name="shippingCost"
		// 					type="number"
		// 					value={formData?.shippingCost || ""}
		// 					onChange={handleChange}
		// 					size="small"
		// 					fullWidth
		// 				/>
		// 				<TextField
		// 					label={t('LABELS.LEAD_TIME')}
		// 					variant="outlined"
		// 					name="leadTime"
		// 					type="number"
		// 					value={formData?.leadTime || ""}
		// 					onChange={handleChange}
		// 					size="small"
		// 					fullWidth
		// 				/>
		// 				<TextField
		// 					label={t('LABELS.TAX_RATE')}
		// 					variant="outlined"
		// 					name="taxRate"
		// 					type="number"
		// 					value={formData?.taxRate || ""}
		// 					onChange={handleChange}
		// 					size="small"
		// 					fullWidth
		// 				/>
		// 			</Box>

		// 			<Typography variant="body1" fontWeight="bold" sx={{ mt: 2, mb: 0.5 }}>
		// 				{t('UI_TEXT.NOTIFICATION_EMAILS', { defaultValue: "Notification Emails" })}
		// 			</Typography>
		// 			<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
		// 				{t('UI_TEXT.NOTIFICATION_EMAILS_DESC', { defaultValue: "In addition to admin notifications for new orders, request lists, and CCR submissions, you can add extra recipient emails here." })}
		// 			</Typography>
		// 			<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
		// 				<TextField
		// 					label={t('LABELS.DEFAULT_ORDER_NOTIFY_EMAILS', { defaultValue: "Default Order Emails" })}
		// 					variant="outlined"
		// 					name="defaultOrderNotifyEmails"
		// 					value={formData?.defaultOrderNotifyEmails || ""}
		// 					onChange={handleChange}
		// 					size="small"
		// 					fullWidth
		// 					helperText={t('UI_TEXT.EMAIL_LIST_HELPER', { defaultValue: "Comma-separated emails. Used if no setting is saved." })}
		// 					placeholder="orders@example.com, ops@example.com"
		// 				/>
		// 				<TextField
		// 					label={t('LABELS.DEFAULT_RL_NOTIFY_EMAILS', { defaultValue: "Default Request List Emails" })}
		// 					variant="outlined"
		// 					name="defaultRlNotifyEmails"
		// 					value={formData?.defaultRlNotifyEmails || ""}
		// 					onChange={handleChange}
		// 					size="small"
		// 					fullWidth
		// 					helperText={t('UI_TEXT.EMAIL_LIST_HELPER', { defaultValue: "Comma-separated emails. Used if no setting is saved." })}
		// 					placeholder="rl@example.com"
		// 				/>
		// 				<TextField
		// 					label={t('LABELS.DEFAULT_CCR_NOTIFY_EMAILS', { defaultValue: "Default CCR Contact Emails" })}
		// 					variant="outlined"
		// 					name="defaultCcrNotifyEmails"
		// 					value={formData?.defaultCcrNotifyEmails || ""}
		// 					onChange={handleChange}
		// 					size="small"
		// 					fullWidth
		// 					helperText={t('UI_TEXT.EMAIL_LIST_HELPER', { defaultValue: "Comma-separated emails. Used if no setting is saved." })}
		// 					placeholder="ccr@example.com"
		// 				/>
		// 			</Box>

		// 		</>
		// 	),
		// },
		{
			key: "inventory",
			label: t('SETTINGS_TABS.INVENTORY_1'),
			permission: 'app_settings.manage_inventory_settings',
			infoKey: 'SETTINGS_TABS.INVENTORY_1',
			content: (
				<>
					<Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
						{t('UI_TEXT.INVENTORY')}
					</Typography>
					<Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, mb: 1 }}>
						<FormControl fullWidth size="small">
							<InputLabel>{t('LABELS.TRACK_INVENTORY')}</InputLabel>
							<Select
								name="trackInventory"
								value={formData?.trackInventory || ""}
								onChange={handleChange}
								label={t('LABELS.TRACK_INVENTORY')}
								size="small">
								<MenuItem value="Enabled">{t('MENU_ITEMS.ENABLED')}</MenuItem>
								<MenuItem value="Disabled">{t('MENU_ITEMS.DISABLED')}</MenuItem>
							</Select>
						</FormControl>
						<FormControl fullWidth size="small">
							<InputLabel>{t('LABELS.BACK_ORDERS')}</InputLabel>
							<Select
								name="backOrders"
								value={formData?.backOrders || ""}
								onChange={handleChange}
								label={t('LABELS.BACK_ORDERS')}
								size="small">
								<MenuItem value="Enabled">{t('MENU_ITEMS.ENABLED')}</MenuItem>
								<MenuItem value="Disabled">{t('MENU_ITEMS.DISABLED')}</MenuItem>
							</Select>
						</FormControl>
					</Box>

					<Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
						{t('UI_TEXT.MULTI_FACTOR_AUTHENTICATION')}
					</Typography>
						<Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
							<FormControl fullWidth size="small">
								<InputLabel>{t('LABELS.MFA_STATUS')}</InputLabel>
								<Select
								name="mfaEnabled"
								value={formData?.mfaEnabled || ""}
								onChange={handleChange}
								label={t('LABELS.MFA_ENABLED')}
								size="small">
									<MenuItem value="Enabled">{t('MENU_ITEMS.ENABLED')}</MenuItem>
									<MenuItem value="Disabled">{t('MENU_ITEMS.DISABLED')}</MenuItem>
								</Select>
							</FormControl>
							<FormControl fullWidth size="small">
								<InputLabel>{t('LABELS.MFA_FREQUENCY_MODE', { defaultValue: "MFA Frequency Mode" })}</InputLabel>
								<Select
									value={isEveryLoginMfaValue(formData?.mfaDays) ? "every_login" : "days"}
									onChange={handleMfaFrequencyModeChange}
									label={t('LABELS.MFA_FREQUENCY_MODE', { defaultValue: "MFA Frequency Mode" })}
									size="small">
									<MenuItem value="every_login">Every Login</MenuItem>
									<MenuItem value="days">Every N Days</MenuItem>
								</Select>
							</FormControl>
							{!isEveryLoginMfaValue(formData?.mfaDays) && (
								<TextField
									label={t('LABELS.MFA_FREQUENCY_DAYS_VALUE', { defaultValue: "MFA Frequency Value (Days)" })}
									variant="outlined"
									name="mfaDays"
									type="number"
									value={formData?.mfaDays || ""}
									onChange={handleChange}
									size="small"
									fullWidth
									inputProps={{ min: 1 }}
								/>
							)}
						</Box>
					</>
				),
			},
		// {
		// 	key: "site",
		// 	label: t('SETTINGS_TABS.SITE_MANAGEMENT'),
		// 	permission: 'app_settings.manage_site_settings',
		// 	infoKey: 'SETTINGS_INFO.SITE',
		// 	content: (
		// 		<>
		// 			<Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
		// 				{t('SETTINGS_TABS.SITE_MANAGEMENT')}
		// 			</Typography>
		// 			<FormControl fullWidth size="small">
		// 				<InputLabel>{t('LABELS.ENABLE_DISCOUNT_FIELD')}</InputLabel>
		// 				<Select
		// 					name="enableDiscountField"
		// 					value={Boolean(formData.enableDiscountField)}
		// 					onChange={handleChange}
		// 					label={t('LABELS.ENABLE_DISCOUNT_FIELD')}
		// 					size="small">
		// 					<MenuItem value={true}>{t('MENU_ITEMS.ENABLED')}</MenuItem>
		// 					<MenuItem value={false}>{t('MENU_ITEMS.DISABLED')}</MenuItem>
		// 				</Select>
		// 			</FormControl>
		// 		</>
		// 	),
		// },
		// {
		// 	key: "reminders",
		// 	label: t('SETTINGS_TABS.REMINDERS'),
		// 	permission: 'app_settings.manage_reminder_settings',
		// 	infoKey: 'SETTINGS_INFO.REMINDERS',
		// 	content: (
		// 		<>
		// 			<Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
		// 				{t('LABELS.GLOBAL_REMINDER')}
		// 			</Typography>
		// 			<Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}>
		// 				<FormControl fullWidth size="small">
		// 					<InputLabel>{t('LABELS.GLOBAL_REMINDER')}</InputLabel>
		// 					<Select
		// 						name="productReminder"
		// 						value={formData?.productReminder || "Disabled"}
		// 						onChange={handleChange}
		// 						label={t('LABELS.GLOBAL_REMINDER')}
		// 						size="small">
		// 						<MenuItem value="Enabled">{t('MENU_ITEMS.ENABLED')}</MenuItem>
		// 						<MenuItem value="Disabled">{t('MENU_ITEMS.DISABLED')}</MenuItem>
		// 					</Select>
		// 				</FormControl>
		// 				<TextField
		// 					label={t('LABELS.MINIMUM_PURCHASE_COUNT_THRESHOLD')}
		// 					variant="outlined"
		// 					name="reminderPurchaseCountThreshold"
		// 					type="number"
		// 					value={formData?.reminderPurchaseCountThreshold || 4}
		// 					onChange={handleChange}
		// 					size="small"
		// 					fullWidth
		// 				/>
		// 				<TextField
		// 					label={t('LABELS.MAXIMUM_REMINDER_COUNT')}
		// 					variant="outlined"
		// 					name="maximumReminderCount"
		// 					type="number"
		// 					value={formData?.maximumReminderCount || 5}
		// 					onChange={handleChange}
		// 					size="small"
		// 					fullWidth
		// 				/>
		// 			</Box>
		// 		</>
		// 	),
		// },
		{
			key: "numberSequences",
			label: t('UI_TEXT.NUMBER_SEQUENCES', { defaultValue: "Number Sequences" }),
			permission: 'app_settings.manage_company_info',
			content: (
				<>
					<Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
						{t('UI_TEXT.NUMBER_SEQUENCES', { defaultValue: "Number Sequences" })}
					</Typography>
					<Grid container spacing={2}>
						<Grid item xs={12} md={6}>
							{renderSequenceCard(
								t('LABELS.ORDER_NUMBER_FORMAT', { defaultValue: "Order Number Format" }),
								"orderNumberConfig",
								DEFAULT_ORDER_SEQUENCE
							)}
						</Grid>
						<Grid item xs={12} md={6}>
							{renderSequenceCard(
								t('LABELS.REQUEST_NUMBER_FORMAT', { defaultValue: "Request List Number Format" }),
								"requestNumberConfig",
								DEFAULT_REQUEST_SEQUENCE
							)}
						</Grid>
					</Grid>
				</>
			),
		},
		{
			key: "cartRetention",
			label: t('SETTINGS_TABS.CART_RETENTION'),
			permission: 'app_settings.manage_cart_retention',
			infoKey: 'SETTINGS_INFO.CART',
			content: (
				<>
					<Typography variant="body1" fontWeight="bold" sx={{ mb: 0.5 }}>
						{t('SETTINGS_TABS.CART_RETENTION')}
					</Typography>
					<FormControlLabel
						control={
							<Switch
								name="cartRetentionEnabled"
								checked={!!formData.cartRetentionEnabled}
								onChange={handleSwitchChange}
							/>
						}
						label={formData.cartRetentionEnabled ? t('TITLES.CART_RETENTION_ON') : t('TITLES.CART_RETENTION_OFF')}
					/>
					<TextField
						label={t('LABELS.CART_RETENTION_DAYS')}
						variant="outlined"
						name="cartRetentionDays"
						type="number"
						value={formData?.cartRetentionDays || 60}
						onChange={handleChange}
						size="small"
						fullWidth
						disabled={!formData.cartRetentionEnabled}
						sx={{ mt: 2 }}
					/>
					{formData.cartRetentionEnabled ? (
						<Alert severity="info" sx={{ mt: 2 }}>
							{t('TITLES.CART_RETENTION_DAYS_INFO')}
							{t('TITLES.NEXT_CART_CLEANUP', {
								date: new Date(Date.now() + (Number(formData.cartRetentionDays || 60) * 24 * 60 * 60 * 1000)).toLocaleDateString()
							})}
						</Alert>
					) : (
						<Alert severity="info" sx={{ mt: 2 }}>
							{t('TITLES.CART_RETENTION_DISABLED_INFO')}
						</Alert>
					)}
				</>
			),
		},
	]), [formData, sequenceConfig, t]);

	const visibleTabs = settingsTabs.filter((tab) => hasPermission?.(tab.permission));

	useEffect(() => {
		if (visibleTabs.length === 0) {
			setActiveTab(0);
			return;
		}
		if (activeTab > visibleTabs.length - 1) {
			setActiveTab(0);
		}
	}, [visibleTabs.length, activeTab]);

	const renderTabs = () => {
		if (!visibleTabs.length) {
			return (
				<Alert severity="info">
					{t('UI_TEXT.NO_SETTINGS_PERMISSION')}
				</Alert>
			);
		}

		const currentTab = visibleTabs[activeTab];

		return (
			<>
				<Tabs
					value={activeTab}
					onChange={(_, value) => setActiveTab(value)}
					variant="scrollable"
					scrollButtons="auto"
					sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}`, mb: 3 }}
				>
					{visibleTabs.map((tab) => (
						<Tab key={tab.key} label={tab.label} />
					))}
				</Tabs>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					{currentTab?.content}
					{currentTab?.infoKey && (
						<ContentInfo>
							<Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', columnGap: 1 }}>
								<HelpOutlineOutlined />
								{t(currentTab.infoKey)}
							</Box>
						</ContentInfo>
					)}
				</Box>
				<Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: 3 }}>
					<Button type="submit" variant="contained" color="primary" startIcon={<Save />}>
						{t("BUTTON.SAVE_CHANGES")}
					</Button>
				</Box>
			</>
		);
	};

return (
		<Box
		sx={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", width: "100%", marginTop: 2 }}>
			<form onSubmit={handleSubmit} style={{ width: "100%" }}>
				{renderTabs()}
			</form>
		</Box>
	);
};

export default DefaultValues;
